<template>
  <v-dialog v-model="internalValue" max-width="620" persistent>
    <v-card>
      <v-card-title class="d-flex align-center text-subtitle-1">
        <v-icon class="mr-2">mdi-swap-horizontal</v-icon>
        {{ $t("multiUser.dialog.title", { entities: $t(entityLabel) }) }}
        <div class="flex-grow-1"></div>
        <v-btn icon @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-select
          v-model="form.action"
          :items="actionItems"
          item-text="text"
          item-value="value"
          :label="$t('multiUser.dialog.action')"
          dense
          outlined
          :disabled="loading"
        />

        <v-select
          v-model="form.selectedIds"
          :items="availableItems"
          :item-text="itemText"
          :item-value="itemValue"
          :label="$t('multiUser.dialog.select', { entities: $t(entityLabel) })"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          :disabled="loading"
          :search-input.sync="entitySearch"
          clearable
        >
          <template v-slot:prepend-item>
            <v-list-item ripple @mousedown.prevent @click="toggleSelectAll">
              <v-list-item-action>
                <v-icon :color="form.selectedIds.length > 0 ? 'primary' : ''">
                  {{ selectAllIcon }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("multiUser.dialog.selectAll") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2" />
          </template>
        </v-select>

        <v-select
          v-model="form.targetClientIds"
          :items="availableCompanies"
          item-text="name"
          item-value="_id"
          :label="$t('multiUser.dialog.accounts')"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          :disabled="loading"
        >
          <template v-slot:item="{ item }">
            <div class="d-flex flex-column">
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="text--secondary text-caption">
                {{ formatCompanySub(item) }}
              </div>
            </div>
          </template>
        </v-select>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">
          {{ $t("general.cancel") }}
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading || !canSubmit"
          @click="submit"
        >
          <v-icon left>
            {{ form.action === "copy" ? "mdi-content-copy" : "mdi-file-move" }}
          </v-icon>
          {{ form.action === "copy" ? $t("general.copy") : $t("general.move") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "multiUserDialog",
  props: {
    value: { type: Boolean, default: false },
    entityType: { type: String, required: true },
    // Items the user can pick from (e.g. employees, categories)
    availableItems: { type: Array, default: () => [] },
    // Which field to show as label
    itemText: { type: String, default: "name" },
    // Which field to use as value (the _id)
    itemValue: { type: String, default: "_id" },
    clients: { type: Array, default: () => [] },
    currentClientId: { type: String, required: true },
    entityLabel: { type: String, default: "multiUser.dialog.object" },
  },
  data() {
    return {
      internalValue: this.value,
      loading: false,
      entitySearch: "",
      form: {
        action: "copy",
        selectedIds: [],
        targetClientIds: [],
      },
    };
  },
  computed: {
    actionItems() {
      return [
        { value: "copy", text: this.$t("general.copy") },
        { value: "move", text: this.$t("general.move") },
      ];
    },
    availableCompanies() {
      return (this.clients || [])
        .filter((c) => c && c._id !== this.currentClientId)
        .sort((a, b) =>
          String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
            sensitivity: "base",
            numeric: true,
          }),
        );
    },
    canSubmit() {
      return (
        !!this.form.action &&
        this.form.selectedIds.length > 0 &&
        this.form.targetClientIds.length > 0
      );
    },
    allSelected() {
      return (
        this.availableItems.length > 0 &&
        this.form.selectedIds.length === this.availableItems.length
      );
    },
    someSelected() {
      return this.form.selectedIds.length > 0 && !this.allSelected;
    },
    selectAllIcon() {
      if (this.allSelected) return "mdi-close-box";
      if (this.someSelected) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
  },
  watch: {
    value(v) {
      this.internalValue = v;
    },
    internalValue(v) {
      this.$emit("input", v);
      if (!v) this.resetForm();
    },
  },
  methods: {
    toggleSelectAll() {
      if (this.allSelected) {
        this.form.selectedIds = [];
      } else {
        this.form.selectedIds = this.availableItems.map(
          (item) => item[this.itemValue],
        );
      }
    },
    formatCompanySub(company) {
      const a = company?.companyData?.address;
      if (!a) return "";
      return [a.postalCode, a.city, a.country].filter(Boolean).join(" · ");
    },
    resetForm() {
      this.loading = false;
      this.entitySearch = "";
      this.form = {
        action: "copy",
        selectedIds: [],
        targetClientIds: [],
      };
    },
    close() {
      this.internalValue = false;
    },
    async submit() {
      if (!this.canSubmit) return;

      try {
        this.loading = true;

        this.$emit("confirm", {
          action: this.form.action,
          entityType: this.entityType,
          entityIds: this.form.selectedIds,
          targetClientIds: this.form.targetClientIds,
        });

        this.close();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
